/* eslint-disable no-underscore-dangle */
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { orderBy, findIndex, without, debounce } from 'lodash';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import { Button } from 'ui';
import Icon from 'ui/Icon';
import { AlertCircle } from 'react-feather';
import MatchingPairRow from './MatchingPairRow';

const DEBOUNCE_CHANGES_DELAY = 300;

const KnowledgeCheckMatching = ({
  choices: unorderedChoices,
  requestUpdateAndApplyChanges,
  applyChanges,
  handleUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [disableAddItem, setDisableAddItem] = useState(false);
  const [duplicateItemIndexes, setDuplicateItemIndexes] = useState([]);
  const choices = useMemo(
    () => orderBy(unorderedChoices, 'position'),
    [unorderedChoices],
  );

  const checkForDuplicates = useCallback(matchingChoices => {
    const items = matchingChoices.map(choice => choice.item.toLowerCase());
    const uniqueItems = [];
    const duplicateIndexes = [];
    items.forEach((item, index) => {
      if (uniqueItems.includes(item)) {
        duplicateIndexes.push(index);
      } else if (item !== '') {
        uniqueItems.push(item);
      }
    });

    setDuplicateItemIndexes(duplicateIndexes);
  }, []);

  useEffect(() => {
    checkForDuplicates(choices);
  }, [checkForDuplicates, choices]);

  const debouncedHandleUpdate = useMemo(() => {
    const asyncUpdate = data => {
      handleUpdate(data).then(() => {
        checkForDuplicates(data.matching_choices);
        setDisableAddItem(false);
      });
    };
    return debounce(asyncUpdate, DEBOUNCE_CHANGES_DELAY);
  }, [handleUpdate, checkForDuplicates]);

  const onUpdateChoice = useCallback(
    choice => {
      setDisableAddItem(true);
      const params = {
        id: choice.id,
        item: choice.item,
        match: choice.match,
        matching_choice_path: choice.matching_choice_path,
        position: choice.position,
      };

      const choicesCopy = [...choices];
      const choiceToUpdateIndex = findIndex(choices, ['id', choice.id]);

      if (choiceToUpdateIndex !== -1 && choice.id !== undefined) {
        choicesCopy.splice(choiceToUpdateIndex, 1, params);
      }

      applyChanges({ choices: choicesCopy }, { mergeChanges: false });
      debouncedHandleUpdate({ matching_choices: choicesCopy });
    },
    [applyChanges, choices, debouncedHandleUpdate],
  );

  const addChoice = useCallback(() => {
    const params = {
      item: '',
      match: '',
      position: choices.length + 1,
    };
    setLoading(true);
    checkForDuplicates([...choices, params]);
    requestUpdateAndApplyChanges({
      matching_choices: [...choices, params],
    }).then(() => setLoading(false));
  }, [requestUpdateAndApplyChanges, choices, checkForDuplicates]);

  const onRemoveChoice = useCallback(
    choice => {
      const choiceCopy = choice;
      delete choiceCopy.position;
      const choicesCopy = orderBy(without(choices, choice), 'position').map(
        (c, index) => ({
          ...c,
          position: index + 1,
        }),
      );
      setLoading(true);
      requestUpdateAndApplyChanges({
        matching_choices: [...choicesCopy, { ...choiceCopy, _destroy: true }],
      }).then(() => setLoading(false));
    },
    [choices, requestUpdateAndApplyChanges],
  );

  const renderMatchingPairRow = useCallback(
    (choice, index) => {
      if (!choice.position) return null;
      const isDuplicate = duplicateItemIndexes.includes(index);
      return (
        <div key={choice.id}>
          <MatchingPairRow
            choice={choice}
            onDelete={c => onRemoveChoice(c)}
            onUpdate={c => onUpdateChoice(c)}
            addChoice={() => {
              if (choices.length < 10) addChoice();
            }}
            loading={loading}
            index={index}
            disableAddItem={disableAddItem}
            isDuplicate={isDuplicate}
          />
          {isDuplicate && (
            <div className="MatchingElement--error">
              <AlertCircle size={12} className="fs-0 mr-8" />
              <span className="text-wrap">
                {t('questions.choices.matching.duplicate_item')}
              </span>
            </div>
          )}
          <hr />
        </div>
      );
    },
    [
      addChoice,
      loading,
      onRemoveChoice,
      onUpdateChoice,
      choices.length,
      disableAddItem,
      duplicateItemIndexes,
    ],
  );

  return (
    <div className="pb-32 pt-24">
      <Text variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}>
        <span>{t('questions.choices.matching.item_label')}</span>
        <span className="c-danger-red"> * </span>
      </Text>
      <div className="mb-12">
        <Text variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD}>
          {t('questions.choices.matching.items_subtitle')}
        </Text>
      </div>
      {choices.map((choice, index) => renderMatchingPairRow(choice, index))}
      {choices.length === 5 && (
        <div className="MatchingAlert d-f items-start p-16">
          <div className="mr-12 mt-2">
            <Icon name="info" />
          </div>
          <div>
            {t('questions.choices.matching.tip')}
            {t('questions.choices.matching.match_tip')}
          </div>
        </div>
      )}
      <div className="MatchingFooter mt-16 d-f fd-r ai-c">
        <Button
          icon="add"
          tall
          onClick={() => {
            addChoice();
          }}
          readOnly={choices.length >= 10 || loading || disableAddItem}
        >
          {t('questions.choices.matching.add_pair')}
        </Button>
      </div>
    </div>
  );
};

export default KnowledgeCheckMatching;
